// src/components/patient/appointments/FilterChips.jsx
//
// Compact filter chips for the appointment list.
//
//   * each chip is a button with an icon, its label and the selected value;
//     it shows an active style when a value is set and an open style while its
//     panel is shown (aria-expanded / aria-controls);
//   * clicking a chip opens its panel directly below the chip row, so it is
//     never clipped on small screens;
//   * Arrow Left/Right/Home/End move between chips (roving toolbar);
//   * Escape or a click outside closes the panel and focus returns to the chip;
//   * removable chips list the active filters, with "Clear all".
//
// Chips carry no data of their own: the page passes values, option lists and
// change handlers, so the existing filter vocabulary/API contract is reused.

import React, { useCallback, useEffect, useId, useRef } from "react";

import { classNames } from "../booking/bookingUi";

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ChipIcon({ name, className = "h-4 w-4" }) {
  const common = { className, viewBox: "0 0 24 24", fill: "none", "aria-hidden": true };
  switch (name) {
    case "status":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
          <path d="m8.5 12 2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "date":
      return (
        <svg {...common}>
          <rect x="4" y="5" width="16" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
          <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "doctor":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
          <path d="M4 20c1.5-4 4.5-6 8-6s6.5 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "clinic":
      return (
        <svg {...common}>
          <path d="M4 20V8l8-4 8 4v12H4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M12 10v6M9 13h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "service":
      return (
        <svg {...common}>
          <path d="M6 4h12v16H6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          <path d="M9 9h6M9 13h6M9 17h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "mode":
      return (
        <svg {...common}>
          <rect x="3" y="7" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="m15 11 5-3v8l-5-3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );
    case "close":
      return (
        <svg {...common}>
          <path d="M7 7l10 10M17 7 7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case "chevron":
      return (
        <svg {...common}>
          <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

/**
 * chips: [{ key, label, icon, valueLabel, active, render: (close) => node }]
 * openKey / onOpenChange(key|null) are controlled by the page.
 */
export default function FilterChips({ chips, openKey, onOpenChange, label = "Filters" }) {
  const baseId = useId();
  const rowRef = useRef(null);
  const panelRef = useRef(null);
  const chipRefs = useRef({});
  const openChip = chips.find((chip) => chip.key === openKey) || null;

  const close = useCallback(
    ({ restoreFocus = true } = {}) => {
      const key = openKey;
      onOpenChange(null);
      if (restoreFocus && key) window.setTimeout(() => chipRefs.current[key]?.focus(), 0);
    },
    [openKey, onOpenChange]
  );

  // Focus moves into the panel when it opens.
  useEffect(() => {
    if (!openKey) return;
    panelRef.current?.querySelector(FOCUSABLE)?.focus();
  }, [openKey]);

  // Escape and outside clicks close the open panel.
  useEffect(() => {
    if (!openKey) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    const onPointerDown = (event) => {
      const inside = rowRef.current?.contains(event.target) || panelRef.current?.contains(event.target);
      if (!inside) close({ restoreFocus: false });
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [openKey, close]);

  function onRowKeyDown(event) {
    const keys = chips.map((chip) => chip.key);
    const index = keys.findIndex((key) => chipRefs.current[key] === document.activeElement);
    if (index < 0) return;
    let next = null;
    if (event.key === "ArrowRight") next = (index + 1) % keys.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + keys.length) % keys.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = keys.length - 1;
    if (next === null) return;
    event.preventDefault();
    chipRefs.current[keys[next]]?.focus();
  }

  return (
    <div>
      <div ref={rowRef} role="toolbar" aria-label={label} onKeyDown={onRowKeyDown} className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => {
          const open = chip.key === openKey;
          return (
            <button
              key={chip.key}
              ref={(node) => {
                chipRefs.current[chip.key] = node;
              }}
              type="button"
              id={`${baseId}-${chip.key}-chip`}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? `${baseId}-${chip.key}-panel` : undefined}
              onClick={() => onOpenChange(open ? null : chip.key)}
              data-testid={`chip-${chip.key}`}
              data-active={chip.active ? "true" : "false"}
              className={classNames(
                "inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                chip.active ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-white text-slate-700 hover:bg-slate-50",
                open && "ring-2 ring-primary/30"
              )}
            >
              <ChipIcon name={chip.icon} />
              <span>{chip.label}</span>
              {chip.valueLabel ? <span className="max-w-[10rem] truncate font-normal">: {chip.valueLabel}</span> : null}
              <ChipIcon name="chevron" className={classNames("h-3.5 w-3.5 transition", open && "rotate-180")} />
            </button>
          );
        })}
      </div>

      {openChip ? (
        <div
          ref={panelRef}
          id={`${baseId}-${openChip.key}-panel`}
          role="dialog"
          aria-labelledby={`${baseId}-${openChip.key}-chip`}
          className="mt-2 w-full max-w-md rounded-2xl border border-border bg-white p-4 shadow-soft"
          data-testid="chip-panel"
        >
          {openChip.render(close)}
        </div>
      ) : null}
    </div>
  );
}

// A single-choice option list for a chip panel. Choosing closes the panel.
export function ChipOptions({ name, value, options, allLabel, onChange, close }) {
  const all = [{ value: "", label: allLabel }, ...(options || [])];
  return (
    <div role="radiogroup" aria-label={name} className="max-h-72 space-y-1 overflow-y-auto">
      {all.map((option) => {
        const checked = String(value || "") === String(option.value);
        return (
          <button
            key={option.value || "__all"}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => {
              onChange(option.value);
              close();
            }}
            className={classNames("flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm", checked ? "bg-primary/10 font-semibold text-primary" : "hover:bg-slate-50")}
          >
            <span className="truncate">{option.label}</span>
            {checked ? <span aria-hidden="true">✓</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function ActiveFilterChips({ items, onClearAll }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" aria-label="Active filters" data-testid="active-filters">
      {items.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 py-1 pl-3 pr-1 text-xs font-semibold text-primary">
          {item.label}: <span className="font-normal">{item.value}</span>
          <button type="button" onClick={item.onRemove} aria-label={`Remove ${item.label} filter`} className="ml-1 rounded-full p-1 hover:bg-primary/10">
            <ChipIcon name="close" className="h-3 w-3" />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClearAll} className="rounded-full px-2 py-1 text-xs font-semibold text-slate-600 underline-offset-2 hover:underline">
        Clear all
      </button>
    </div>
  );
}
